import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import accountMapQueryKeys from './accountMapQueryKeys'
import { updateAccountMapRules } from '../service/accountMapService'
import { AccountMapRule } from '../types/AccountMapRule'
import { Company } from '../../../types/company/Company'

interface UpdateAccountMapRulesParams {
  companyId: Company['id']
  rules: AccountMapRule[]
}

export const useUpdateAccountMapRules = () => {
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ companyId, rules }: UpdateAccountMapRulesParams) => updateAccountMapRules(companyId, rules),

    onMutate: async ({ companyId, rules }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: accountMapQueryKeys.list(companyId) })

      // Snapshot previous value
      const previousRules = queryClient.getQueryData<AccountMapRule[]>(accountMapQueryKeys.list(companyId))

      // Optimistically update to the new value
      queryClient.setQueryData<AccountMapRule[]>(accountMapQueryKeys.list(companyId), rules)

      return { previousRules, companyId }
    },

    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousRules) {
        queryClient.setQueryData(accountMapQueryKeys.list(context.companyId), context.previousRules)
      }
    },

    onSuccess: (data, { companyId }) => {
      // Show success notification
      message.success(t('messages:updateSuccess', 'Successfully updated'))

      // Invalidate to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: accountMapQueryKeys.list(companyId) })
    }
  })
}
